import { useRollbar } from '@rollbar/react'
import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { Taxcode } from '../../models/Taxcode'
import { apiClient } from '../../modules/apiclient'
import { stringEqual } from '../../utils'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { learnMoreComponents } from './LearnMore'
import { LocationModal } from './LocationModal'

const render = ({ base }: PocketComponentProps<void>) => {
    const rollbar = useRollbar()
    const owner = base.owner
    const navigate = usePSNavigate()
    const [showBusinessEdit, setShowBusinessEdit] = useState(false)
    const [showLearnMore, setShowLearnMore] = useState(false)
    const [showInvalidAddress, setShowInvalidAddress] = useState(false)

    const [shouldCollectTax, setShouldCollectTax] = useState<boolean | undefined>()
    const [taxPercentage, setTaxPercentage] = useState(0)
    const [fetchingTaxCode, setFetchingTaxCode] = useState(false)
    const [address, setAddress] = useState<string | undefined>()
    const [taxCode, setTaxCode] = useState<string | undefined>()

    const getTaxInfo = useCallback(
        debounce((userId: string) => {
            if (fetchingTaxCode) return

            setFetchingTaxCode(true)
            return apiClient
                .get(`/user/${userId}/taxcode`)
                .then((response) => response.data)
                .then((result) => {
                    setTaxPercentage(parseFloat(result?.rate ?? ''))
                    setTaxCode(result?.id ?? '')
                })
                .catch((error) => {
                    rollbar.error('getTaxInfo error', { error, taxAddress: owner?.taxAddress })

                    if (!owner?.taxcode?.id && owner?.taxAddress) {
                        return findTaxCode(owner.taxAddress)
                    }
                    else {
                        setTaxCode('')
                        setTaxPercentage(0)
                    }
                })
                .finally(() => setFetchingTaxCode(false))
        }, 250),
        [base.me]
    )

    const findTaxCode = useCallback(
        debounce(async (location: string) => {
            setFetchingTaxCode(true)
            const result = await RecordFactory.getInstance().queryTaxcode(location)
            setFetchingTaxCode(false)

            if (!result.ok) {
                base.logAndDisplayError(result.error)
                return
            }
            if (result.value === null) {
                setShowInvalidAddress(true)
                return
            }

            setAddress(
                [
                    result.value.street?.trim() ?? '',
                    result.value.city,
                    result.value.state,
                    result.value.postalCode,
                ]
                    .filter(Boolean)
                    .join(', ')
                    .trim()
            )
            if (result.value.rate !== undefined) {
                setTaxPercentage(result.value.rate)
            }
            setTaxCode(result.value.id)
            setShowBusinessEdit(false)
        }, 250),
        []
    )

    const onSave = async () => {
        setShowBusinessEdit(false)

        const newUser = base.me.clone()

        newUser.taxAddress = address ?? ''
        if (taxCode !== undefined) {
            const newTaxcode = new Taxcode({ id: taxCode })
            newUser.taxcode = newTaxcode
        }
        else {
            newUser.taxcode = undefined
        }
        newUser.alwaysCollectTax = shouldCollectTax ?? false

        base.showProgressBar('Updating account...')

        const result = await RecordFactory.getInstance().submitTaxcode(newUser)

        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }

        navigate('/settings')
    }

    const onLocationModalDone = (location: string) => {
        setTaxCode(undefined)
        findTaxCode(location)
    }

    const hasEdits =
        owner.alwaysCollectTax !== shouldCollectTax ||
        !stringEqual(owner.taxAddress, address) ||
        !stringEqual(owner.taxcode?.id, taxCode)
    const saveEnabled = !fetchingTaxCode && hasEdits

    useEffect(() => {
        if (shouldCollectTax === undefined) {
            setShouldCollectTax(owner.alwaysCollectTax || false)
        }
        if (address === undefined) setAddress(owner.taxAddress ?? '')
        if (taxCode === undefined && owner.id !== undefined) getTaxInfo(owner.id)
    }, [owner, shouldCollectTax, setShouldCollectTax, address, setAddress, taxCode, getTaxInfo])

    return (
        <WorkflowPage
            title="Setup sales tax"
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="primary" disabled={!saveEnabled} onClick={onSave}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start">
                <WorkflowPage.Card>
                    <WorkflowPage.IconSection
                        title="Your business location"
                        icon="markerPin.large"
                        hasHoverEffect
                        thirdLine={
                            <div className="text-secondary-400 font-normal">
                                {taxPercentage}% • {address}
                            </div>
                        }
                        secondaryView={
                            <Button
                                intent="tertiaryLink"
                                size="small"
                                onClick={() => setShowBusinessEdit(true)}
                            >
                                Edit
                            </Button>
                        }
                    />

                    <WorkflowPage.IconSection
                        title="Always collect sales tax"
                        icon="percentage2.large"
                        description="Collect sales tax on every transaction"
                        hasHoverEffect
                        secondaryView={
                            <Checkbox isChecked={shouldCollectTax} onChange={setShouldCollectTax} />
                        }
                    >
                        <button
                            className="text-primary-700 hover:text-primary-700/60 text-sm underline"
                            onClick={(e) => setShowLearnMore(true)}
                        >
                            Learn more
                        </button>
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            {showBusinessEdit && (
                <LocationModal
                    filterAddresses={true}
                    onClose={() => setShowBusinessEdit(false)}
                    onDone={onLocationModalDone}
                    currentLocation={address ?? ''}
                />
            )}

            <Modal isOpen={showLearnMore} title="Sales Tax" onClose={() => setShowLearnMore(false)}>
                <div className="text-gray-600">{learnMoreComponents['sales-tax'].body}</div>
            </Modal>

            <Modal
                isOpen={showInvalidAddress}
                title="Please try again"
                onClose={() => setShowInvalidAddress(false)}
                footer={
                    <Button intent="primary" onClick={() => setShowInvalidAddress(false)}>
                        OK
                    </Button>
                }
            >
                Enter a valid address with zipcode so that PocketSuite can calculate your sales tax
                rate.
            </Modal>

            <NavigationBlockerPrompt when={hasEdits} />
        </WorkflowPage>
    )
}

export const SetupSalesTaxPage = usePSComponent(render)
